import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import FloatingLabelInput from './PlaceHolder';
import { sanitizeAadharInput, validateAadharNumber, formatAadharDisplay } from './formatDisplay';
import logo from './assets/myeats-logo.png';
import bike from './assets/bike.png';
import restaurant from './assets/Restaurant.png';
import children from './assets/children.png';

import './css/SignUp.css'; // This will now contain the shared Signin/Signup styles

const PASSWORD_RULE_MESSAGE =
  'Password must start with an uppercase letter, be at least 6 characters, and include lowercase, number, and special character.';
const PASSWORD_RULE_REGEX = /^[A-Z](?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z\d]).{5,}$/;
const extractApiErrorMessage = (error, fallback = 'An error occurred') => {
  const data = error?.response?.data;
  const message = data?.message || data?.error || data?.details || error?.message;
  if (Array.isArray(message)) return message.join(', ');
  return String(message || fallback);
};


const Signup = ({ embedded = false, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [aadharNo, setAadharNo] = useState('');
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();

  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, ''); // Allow only numbers
    const newOtp = [...otp];
  
    if (value === '') {
      // Clear current box and move to the previous box if backspace
      newOtp[index] = '';
      setOtp(newOtp);
  
      if (element.previousSibling) {
        element.previousSibling.focus();
      }
    } else if (value.length === 1) {
      // Update the current box
      newOtp[index] = value;
      setOtp(newOtp);
  
      // Move to the next box if available
      if (element.nextSibling) {
        element.nextSibling.focus();
      }
    } else {
      // If user pastes more than one character, ignore the input
      element.value = otp[index];
    }
  };
  
  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !otp[index]) {
      // If the current box is empty and backspace is pressed, move to the previous box
      if (event.target.previousSibling) {
        event.target.previousSibling.focus();
      }
    }
  };

  const requestOtp = async () => {
    const aadharError = validateAadharNumber(aadharNo);
    if (aadharError) {
      alert(aadharError);
      return;
    }
    try {
      const response = await axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsRequest, { 
        identifier: email.trim(),
        mobile: mobile.trim(),
        purpose: "signup"
      });
  
      alert(response.data.message);
      setStep(2);
    } catch (error) {
      console.error('Request OTP Error:', error.response || error);
      const message = extractApiErrorMessage(error, 'An error occurred');
      if (/mobile.*already\s*exists|already\s*exists.*mobile|mobile number.*exists/i.test(message)) {
        alert('This mobile number is already registered. Please sign in.');
        return;
      }
      if (/email.*already\s*exists|already\s*exists.*email/i.test(message)) {
        alert('This email is already registered. Please sign in.');
        return;
      }
      alert(message);
    }
  };

  const verifyOtp = async () => {
    try {
      const otpString = otp.join('');
      await axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsVerify, {
        identifier: email,
        otp: otpString,
      });
      // alert('OTP verified');
      registerUser();
    } catch (error) {
      alert(error.response?.data?.message || 'Invalid OTP');
    }
  };
  

  const registerUser = async () => {
    try {
      const response = await axiosRestaurantInstance.post('restaurant/admin', {
        firstName: firstName,
        lastName: lastName,
        mobile,
        email,
        aadharNo: sanitizeAadharInput(aadharNo),
        password,
        
      });
      const createdUser = response?.data?.user || null;

      if (embedded) {
        alert('Root user created successfully');
        if (typeof onComplete === 'function') {
          onComplete({
            userId: createdUser?.userId,
            firstName: createdUser?.firstName || firstName,
            lastName: createdUser?.lastName || lastName,
            mobile: createdUser?.mobile || mobile,
            email: createdUser?.email || email,
          });
        }
        return;
      }
      navigate('/restaurant/signin');
    } catch (error) {
      const message = extractApiErrorMessage(error, 'Failed to register');
      if (/mobile.*already\s*exists|already\s*exists.*mobile|mobile number.*exists/i.test(message)) {
        alert('This mobile number is already registered. Please sign in.');
        if (!embedded) navigate('/restaurant/signin');
        return;
      }
      if (/email.*already\s*exists|already\s*exists.*email/i.test(message)) {
        alert('This email is already registered. Please sign in.');
        if (!embedded) navigate('/restaurant/signin');
        return;
      }
      alert(message);
    }
  };

  const submitSignup = async () => {
    if (embedded) {
      await registerUser();
      return;
    }

    await requestOtp();
  };

  // Step 1: User details form
  const renderStep1 = () => (
    <div className={`signup_form ${embedded ? "signup_form--embedded" : ""}`}>
    <h3 className='signup_heading' style={{marginTop:'15px'}}>Restaurant</h3>
      <div className={`signup_fields_grid ${embedded ? "signup_fields_grid--embedded" : ""}`}>
      <FloatingLabelInput
  type="text"
  value={firstName}
  onChange={(e) => setFirstName(e.target.value)}
  label="First Name"
  isRequired={true}
/>
<FloatingLabelInput
  type="text"
  value={lastName}
  onChange={(e) => setLastName(e.target.value)}
  label="Last Name"
  isRequired={true}
/>
<FloatingLabelInput
  type="text"
  value={mobile}
  onChange={(e) => setMobile(e.target.value)}
  label="Mobile Number"
  isRequired={true}
/>
<FloatingLabelInput
  type="email"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  label="Email"
  isRequired={true}
/>
<FloatingLabelInput
  type="text"
  value={formatAadharDisplay(aadharNo)}
  onChange={(e) => setAadharNo(sanitizeAadharInput(e.target.value))}
  label="Aadhar Number"
  isRequired={true}
  maxLength={14}
  inputMode="numeric"
/>
<FloatingLabelInput
  type="password"
  value={password}
  onChange={(e) => setPassword(e.target.value)}
  label="Password"
  isRequired={true}
/>
<FloatingLabelInput
  type="password"
  value={confirmPassword}
  onChange={(e) => setConfirmPassword(e.target.value)}
  label="Confirm Password"
  isRequired={true}
/>
      </div>
      <button className="signup_button" onClick={submitSignup}>
        {embedded ? "Create Root User" : "Sign Up"}
      </button>
      {!embedded ? (
        <div className="signup_signup" style={{marginLeft:'5px'}}>
          <span> Already have an account?</span> <a href="/restaurant/signin" className="signup_link"  style={{
                   fontSize: '14px',
                   fontWeight: 'bold',
                   color: '#007bff',
                   textAlign: 'center',
                   textDecoration: 'none',
                   marginLeft:'15px'
               }}>Sign In</a><a href="https://junioreats.co/" className="signup_link" style={{marginLeft:'15px'}}>Home</a>
        </div>
      ) : null}
    </div>
  );

  // Step 2: OTP verification form
  const renderStep2 = () => (
    <div className="signup-otp-form-container">
      <p className="signup-otp-instruction">Enter the OTP sent to your email</p>
  
      {/* OTP Input Boxes */}
      <div className="signup-otp-box-container">
        {otp.map((value, index) => (
          <input
            key={index}
            type="text"
            maxLength="1"
            className="signup-otp-box"
            value={value}
            onChange={(e) => handleChange(e.target, index)}
            onKeyDown={(e) => handleKeyDown(e, index)} // New handler for backspace
            onFocus={(e) => e.target.select()} // Automatically selects the box content
          />
        ))}
      </div>
  
      <button className="signup-otp-verify-button" onClick={verifyOtp}>
        Verify OTP
      </button>
  
      <p className="signup-otp-resend-text">
        Didn’t receive the OTP?{" "}
        <button type="button" className="signup-otp-resend-link" onClick={requestOtp}>
          Resend OTP
        </button>
      </p>
    </div>
  );
  

  if (embedded) {
    return (
      <div className="signup_content signup_content--embedded" style={{ maxWidth: 900, margin: '0 auto' }}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </div>
    );
  }

  return (
    <div className="signup_container">
      <div className="signup_leftSection">
        {/* Optional images for visuals */}
        <img src={children} alt="Children" className="signup_children" />
        <img
         src={restaurant} 
         alt="Restaurant" 
         className="signup_restaurant" 
         />
        
        <img src={bike} alt="Bike" className="signup_bike" />
      </div>
      <div className="signup_rightSection">
        <div className="signup_logo">
          <img src={logo} alt="My Eats" />
        </div>
        <div className="signup_content">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        </div>
      </div>
    </div>
  );
};

export default Signup;
